import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import axios from '../../../services/AxiosInstance';
import { useParams, useHistory } from 'react-router-dom';
import swal from "sweetalert";
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { getApiUrl } from '../../../utils/ApiUtils';
import "react-datepicker/dist/react-datepicker.css";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Calendar, Landmark, Scale, Settings, Trash2, Edit, Save, Plus, TrendingUp, AlertCircle, Info } from 'lucide-react';
import sv from 'date-fns/locale/sv';
import { toast } from 'sonner';

import PageHeader from '../../layouts/PageHeader';
import { StatStrip, StatCard } from '../Common/StatStrip';
import { brandSelectTheme } from '../Common/reactSelectTheme';

// 38 = $ll-control-height in _list-toolbar.scss — the one control height.
const SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
  option: (base) => ({ ...base, fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const FiscalYearDetails = () => {
  const { companyId, fiscalYearId } = useParams();
  const [fiscalYear, setFiscalYear] = useState({});
  const [openingBalances, setOpeningBalances] = useState([]);
  const [newOpeningLiabilityBalances, setNewOpeningLiabilityBalances] = useState([]);
  const [liabilityAccountsOptions, setLiabilityAccountsOptions] = useState([]);
  const [newOpeningAssetsBalances, setNewOpeningAssetsBalances] = useState([]);
  const [assetsAccountsOptions, setAssetsAccountsOptions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [closingDate, setClosingDate] = useState(null);
  const [selectedAccountingMethod, setSelectedAccountingMethod] = useState(null);
  const [selectedVatPeriod, setSelectedVatPeriod] = useState(null);
  const history = useHistory();
  const [deletedBalances, setDeletedBalances] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isProvisional, setIsProvisional] = useState(false);

  const accountingMethodOptions = [
    { value: 'accrual', label: 'Fakturametoden' },
    { value: 'cash', label: 'Kontantmetoden' }
  ];

  const vatPeriodOptions = [
    { value: 'monthly', label: 'Varje månad' },
    { value: 'quarterly', label: 'En gång per kvartal' },
    { value: 'yearly', label: 'En gång per år' }
  ];

  const fmtSek = (n) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(n);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchFiscalYearDetails(),
        fetchOpeningBalances(),
        fetchAccountsOptions('Eget kapital och skulder').then(setLiabilityAccountsOptions),
        fetchAccountsOptions('Tillgångar').then(setAssetsAccountsOptions)
      ]);
    };
    fetchData();
  }, [companyId, fiscalYearId]);

  const fetchAccountsOptions = async (type) => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/accounts?accountType=${type}`), { withCredentials: true });
      return result.data.accounts.map(account => ({ value: account.account_id, label: `${account.account_number} - ${account.account_name}` }));
    } catch (error) {
      console.error('Error fetching accounts options:', error);
    }
  };

  // Totals are DERIVED from the row arrays on every render, never stored.
  // The old code kept them in state and had every add/change/remove handler
  // recompute them by hand — the remove-new-row handlers forgot to, so a
  // deleted row's amount stuck in the Differens until reload.
  const sumRows = (rows) => rows.reduce((acc, balance) => acc + parseFloat(balance.opening_balance || 0), 0);
  const round2 = (n) => Math.round(n * 100) / 100;

  const fetchFiscalYearDetails = async () => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}`), { withCredentials: true });
      const fiscalYearData = result.data.fiscal_year;

      setFiscalYear(fiscalYearData);
      setStartDate(new Date(fiscalYearData.start_date));
      setEndDate(new Date(fiscalYearData.end_date));
      setClosingDate(fiscalYearData.closing_date ? new Date(fiscalYearData.closing_date) : null);
      setSelectedAccountingMethod(accountingMethodOptions.find(option => option.value === fiscalYearData.accounting_method));
      setSelectedVatPeriod(vatPeriodOptions.find(option => option.value === fiscalYearData.vat_period));
    } catch (error) {
      toast.error('Kunde inte hämta räkenskapsåret');
    }
  };

  const fetchOpeningBalances = async () => {
    try {
      const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}/opening-balances`), { withCredentials: true });
      const openingBalancesData = result.data.opening_balances;
      setIsProvisional(result.data.is_provisional || false);

      setOpeningBalances(openingBalancesData);

      const assetsBalances = openingBalancesData.filter(balance => balance.account_number < 2000);
      const liabilitiesEquityBalances = openingBalancesData.filter(balance => balance.account_number >= 2000);

      setAssets(assetsBalances);
      setLiabilities(liabilitiesEquityBalances);
    } catch (error) {
      console.error('Error fetching opening balances:', error);
      // Don't show error for opening balances - they might not exist yet
      setOpeningBalances([]);
      setAssets([]);
      setLiabilities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        accounting_method: selectedAccountingMethod.value,
        vat_period: selectedVatPeriod.value,
      };

      await axios.put(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}`), data, { withCredentials: true });

      toast.success('Räkenskapsåret har uppdaterats');

      setIsEditing(false);
      fetchFiscalYearDetails();
    } catch (error) {
      toast.error('Kunde inte uppdatera räkenskapsåret');
    }
  };

  const handleDelete = async () => {
    swal({
      title: "Radera räkenskapsåret?",
      text: "Räkenskapsåret och all bokföring som hör till det raderas permanent. Detta går inte att ångra.",
      icon: "warning",
      buttons: ["Avbryt", "Radera"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}`), { withCredentials: true });

          toast.success('Räkenskapsåret har raderats');

          history.push(`/${companyId}/settings/fiscal-years`);
        } catch (error) {
          toast.error('Kunde inte radera räkenskapsåret');
        }
      }
    });
  };

  const handleDateChange = (setDate) => (date) => {
    const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setDate(adjustedDate);
  };

  const saveOpeningBalances = async () => {
    if (!isBalanced) {
      toast.error('Tillgångar och skulder/eget kapital måste vara lika stora.');
      return;
    }

    try {
      const validOpeningBalances = newOpeningAssetsBalances.concat(newOpeningLiabilityBalances).filter(
        balance => balance.account_id && balance.opening_balance !== 0
      );

      const data = {
        openingBalances: validOpeningBalances,
        deletedBalances: deletedBalances
      };

      const response = await axios.post(getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}/opening-balances`),
        data,
        { withCredentials: true });

      if (response.data.success) {
        toast.success('Ingående balanser sparade');

        setNewOpeningAssetsBalances([]);
        setNewOpeningLiabilityBalances([]);
        setDeletedBalances([]);

        fetchOpeningBalances();
      }
    } catch (error) {
      toast.error('Kunde inte spara ingående balanser');
    }
  };

  const removeFetchedAssetBalance = (balance, index) => {
    setAssets(assets.filter((_, i) => i !== index));
    setDeletedBalances([...deletedBalances, balance.account_id]);
  };

  const handleAssetAccountChange = (index, selectedOption) => {
    const updatedBalances = [...newOpeningAssetsBalances];
    const [account_number, account_name] = selectedOption.label.split(' - ');
    updatedBalances[index].account_id = selectedOption.value;
    updatedBalances[index].account_name = account_name;
    updatedBalances[index].account_number = account_number;
    setNewOpeningAssetsBalances(updatedBalances);
  };

  const handleAssetBalanceChange = (index, event) => {
    const updatedBalances = [...newOpeningAssetsBalances];
    updatedBalances[index].opening_balance = event.target.value;
    setNewOpeningAssetsBalances(updatedBalances);
  };

  const removeNewAssetBalanceRow = (index) => {
    const updatedBalances = newOpeningAssetsBalances.filter((_, i) => i !== index);
    setNewOpeningAssetsBalances(updatedBalances);
  };

  const addNewAssetsBalanceRow = () => {
    setNewOpeningAssetsBalances([...newOpeningAssetsBalances, { account_id: '', account_name: '', opening_balance: 0 }]);
  };

  const removeFetchedLiabilityBalance = (balance, index) => {
    setLiabilities(liabilities.filter((_, i) => i !== index));
    setDeletedBalances([...deletedBalances, balance.account_id]);
  };

  const handleLiabilityAccountChange = (index, selectedOption) => {
    const updatedBalances = [...newOpeningLiabilityBalances];
    const [account_number, account_name] = selectedOption.label.split(' - ');
    updatedBalances[index].account_id = selectedOption.value;
    updatedBalances[index].account_name = account_name;
    updatedBalances[index].account_number = account_number;
    setNewOpeningLiabilityBalances(updatedBalances);
  };

  const handleLiabilityBalanceChange = (index, event) => {
    const updatedBalances = [...newOpeningLiabilityBalances];
    updatedBalances[index].opening_balance = event.target.value;
    setNewOpeningLiabilityBalances(updatedBalances);
  };

  const removeNewLiabilityBalanceRow = (index) => {
    const updatedBalances = newOpeningLiabilityBalances.filter((_, i) => i !== index);
    setNewOpeningLiabilityBalances(updatedBalances);
  };

  const addNewLiabilityBalanceRow = () => {
    setNewOpeningLiabilityBalances([...newOpeningLiabilityBalances, { account_id: '', account_name: '', opening_balance: 0 }]);
  };

  const totalAssets = round2(sumRows(assets) + sumRows(newOpeningAssetsBalances));
  const totalLiabilitiesEquity = round2(sumRows(liabilities) + sumRows(newOpeningLiabilityBalances));
  const isBalanced = Math.abs(Math.abs(totalAssets) - Math.abs(totalLiabilitiesEquity)) < 0.005;

  const renderBalanceTable = (rows, newRows, options, onAccountChange, onBalanceChange, onRemoveFetched, onRemoveNew, totalLabel, total) => (
    <div className="fyd-table">
      <table>
        <thead>
          <tr>
            <th>Konto</th>
            <th>Kontonamn</th>
            <th className="text-end">Ingångssaldo</th>
            <th style={{ width: 56 }} aria-label="Åtgärder"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((balance, index) => (
            <tr key={index}>
              <td className="fw-semibold">{balance.account_number}</td>
              <td>{balance.account_name}</td>
              <td className="text-end fw-semibold">{fmtSek(balance.opening_balance)}</td>
              <td className="text-end">
                <button
                  type="button"
                  className="fyd-icon-btn"
                  title="Ta bort"
                  onClick={() => onRemoveFetched(balance, index)}
                >
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
          {newRows.map((balance, index) => (
            <tr key={`new-${index}`}>
              <td colSpan="2">
                <Select
                  options={options}
                  value={options.find(option => option.value === balance.account_id) || null}
                  onChange={(selectedOption) => onAccountChange(index, selectedOption)}
                  placeholder="Välj konto…"
                  theme={brandSelectTheme}
                  styles={SELECT_STYLES}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control fyd-amount"
                  style={{ height: 38, textAlign: 'right' }}
                  value={balance.opening_balance}
                  onChange={(event) => onBalanceChange(index, event)}
                  // Scroll over a focused number input silently changes the
                  // value — for a balance amount that's a data-corruption
                  // trap, so drop focus and let the page scroll instead.
                  onWheel={(event) => event.currentTarget.blur()}
                  placeholder="0,00"
                />
              </td>
              <td className="text-end">
                <button
                  type="button"
                  className="fyd-icon-btn"
                  title="Ta bort raden"
                  onClick={() => onRemoveNew(index)}
                >
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
          <tr className="fyd-total-row">
            <td colSpan="2">{totalLabel}</td>
            <td className="text-end">{fmtSek(total)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laddar…</span>
          </div>
          <p className="mt-2 text-muted">Laddar räkenskapsåret…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page-scoped styles only — modern-card/section-title/page-masthead
          come from the global stylesheet. The old local <style> redefined
          those classnames with its own blue palette and hover-lift, which
          both clashed with the app and leaked onto every mounted page. */}
      <style>{`
        .fyd-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.25rem;
        }
        .fyd-label {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-weight: 600;
          color: var(--title, #1e293b);
          margin-bottom: 0.4rem;
          font-size: 0.8125rem;
        }
        .fyd-grid .react-datepicker-wrapper { width: 100%; }
        .fyd-amount::-webkit-outer-spin-button,
        .fyd-amount::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .fyd-amount { -moz-appearance: textfield; appearance: textfield; }
        .fyd-grid .form-control { height: 38px; }
        .fyd-status {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.3rem 0.85rem;
          border-radius: 999px;
          font-size: 0.8125rem;
          font-weight: 600;
        }
        .fyd-status.open { background: var(--rgba-primary-1); color: var(--primary); }
        .fyd-status.closed { background: #f1f5f9; color: var(--secondary, #64748b); }
        .fyd-status .dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
        .fyd-table {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 0.5rem;
        }
        .fyd-table table { width: 100%; margin: 0; border-collapse: collapse; }
        .fyd-table th {
          background: #f8fafc;
          padding: 0.65rem 1rem;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--secondary, #64748b);
          text-align: left;
        }
        .fyd-table th.text-end { text-align: right; }
        .fyd-table td {
          padding: 0.6rem 1rem;
          border-top: 1px solid #f1f5f9;
          font-size: 0.875rem;
          vertical-align: middle;
        }
        .fyd-table tbody tr:hover { background: #f8fafc; }
        .fyd-total-row td {
          background: #f8fafc;
          border-top: 2px solid #e2e8f0;
          font-weight: 700;
        }
        .fyd-icon-btn {
          background: none;
          border: none;
          padding: 4px;
          color: var(--secondary, #94a3b8);
          cursor: pointer;
          border-radius: 6px;
        }
        .fyd-icon-btn:hover { color: #dc2626; background: #fef2f2; }
        .fyd-add-row {
          background: none;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          color: var(--primary);
          font-weight: 600;
          font-size: 0.875rem;
          padding: 0.4rem 0;
          margin-bottom: 1.5rem;
          cursor: pointer;
        }
        .fyd-add-row:hover { text-decoration: underline; }
        .fyd-banner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 0.85rem 1.25rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          margin-top: 0.5rem;
        }
        .fyd-banner.ok { background: var(--rgba-primary-1); border: 1px solid var(--rgba-primary-2); color: var(--primary); }
        .fyd-banner.off { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
        .fyd-note {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          padding: 0.85rem 1.25rem;
          border-radius: 8px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: var(--secondary, #475569);
          font-size: 0.8125rem;
          margin-bottom: 1.25rem;
        }
        .fyd-note svg { flex-shrink: 0; margin-top: 1px; color: var(--primary); }
      `}</style>

      <div className='col-xl-12'>
        <PageHeader
          icon={Calendar}
          title={`Räkenskapsår ${fiscalYear.year}`}
          subtitle="Inställningar och ingående balans för räkenskapsåret."
          backTo={{ to: `/${companyId}/settings/fiscal-years`, label: 'Inställningar · Räkenskapsår' }}
          actions={(
            <div className="d-flex align-items-center gap-2">
              <span className={`fyd-status ${fiscalYear.state === 'open' ? 'open' : 'closed'}`}>
                <span className="dot" />
                {fiscalYear.state === 'open' ? 'Öppet' : 'Stängt'}
              </span>
              {!isEditing ? (
                <>
                  <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                    <Edit size={14} className="me-1" />
                    Redigera
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={handleDelete}>
                    <Trash2 size={14} className="me-1" />
                    Radera
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline-secondary" size="sm" onClick={() => setIsEditing(false)}>
                    Avbryt
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSave}>
                    <Save size={14} className="me-1" />
                    Spara
                  </Button>
                </>
              )}
            </div>
          )}
        />

        {/* Settings */}
        <div className="modern-card">
          <div className="modern-card-header">
            <div>
              <h4 className="section-title">
                <Settings size={20} />
                Inställningar
              </h4>
              <p className="section-subtitle">
                Period, redovisningsmetod och momsperiod för året
              </p>
            </div>
          </div>
          <div className="modern-card-body">
            <div className="fyd-grid">
              <div>
                <label className="fyd-label">Startdatum</label>
                <DatePicker
                  selected={startDate}
                  onChange={handleDateChange(setStartDate)}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  locale={sv}
                  disabled={!isEditing}
                />
              </div>

              <div>
                <label className="fyd-label">Slutdatum</label>
                <DatePicker
                  selected={endDate}
                  onChange={handleDateChange(setEndDate)}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  locale={sv}
                  disabled={!isEditing}
                />
              </div>

              <div>
                <label className="fyd-label">
                  Redovisningsmetod
                  <Info
                    size={14}
                    className="text-muted"
                    data-tooltip-id="redovisningsmetod"
                    data-tooltip-content="Du kan se vilken metod du har på verksamt.se"
                  />
                </label>
                <Select
                  options={accountingMethodOptions}
                  value={selectedAccountingMethod}
                  onChange={setSelectedAccountingMethod}
                  isDisabled={!isEditing}
                  theme={brandSelectTheme}
                  styles={SELECT_STYLES}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>

              <div>
                <label className="fyd-label">
                  Momsperiod
                  <Info
                    size={14}
                    className="text-muted"
                    data-tooltip-id="momsperiod"
                    data-tooltip-content="Du kan se vilken momsperiod du har på verksamt.se"
                  />
                </label>
                <Select
                  options={vatPeriodOptions}
                  value={selectedVatPeriod}
                  onChange={setSelectedVatPeriod}
                  isDisabled={!isEditing}
                  theme={brandSelectTheme}
                  styles={SELECT_STYLES}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Opening balances */}
        <div className="modern-card">
          <div className="modern-card-header">
            <div>
              <h4 className="section-title">
                <TrendingUp size={20} />
                Ingående balans
              </h4>
              <p className="section-subtitle">
                Tillgångar och skulder vid räkenskapsårets början — summorna måste balansera
              </p>
            </div>
          </div>

          <div className="modern-card-body">
            {isProvisional && (
              <div className="fyd-note">
                <AlertCircle size={16} />
                <span>
                  <strong>Preliminära ingående balanser.</strong>{' '}
                  Balanserna beräknas löpande från föregående år, som ännu inte är stängt,
                  och fastställs när det året stängs.
                </span>
              </div>
            )}

            <StatStrip className="mb-4">
              <StatCard
                icon={TrendingUp}
                label="Totala tillgångar"
                value={fmtSek(totalAssets)}
              />
              <StatCard
                icon={Landmark}
                label="Skulder & eget kapital"
                value={fmtSek(Math.abs(totalLiabilitiesEquity))}
              />
              <StatCard
                icon={Scale}
                label="Differens"
                value={fmtSek(totalAssets - Math.abs(totalLiabilitiesEquity))}
                tone={isBalanced ? 'primary' : 'danger'}
                sub={isBalanced ? 'Balansen stämmer' : 'Balansen stämmer inte'}
              />
            </StatStrip>

            <h5 className="fw-semibold mb-2" style={{ color: 'var(--title, #1e293b)', fontSize: '0.9375rem' }}>Tillgångar</h5>
            {renderBalanceTable(
              assets, newOpeningAssetsBalances, assetsAccountsOptions,
              handleAssetAccountChange, handleAssetBalanceChange,
              removeFetchedAssetBalance, removeNewAssetBalanceRow,
              'Summa tillgångar', totalAssets
            )}
            <button type="button" className="fyd-add-row" onClick={addNewAssetsBalanceRow}>
              <Plus size={15} />
              Lägg till tillgång
            </button>

            <h5 className="fw-semibold mb-2" style={{ color: 'var(--title, #1e293b)', fontSize: '0.9375rem' }}>Skulder och eget kapital</h5>
            {renderBalanceTable(
              liabilities, newOpeningLiabilityBalances, liabilityAccountsOptions,
              handleLiabilityAccountChange, handleLiabilityBalanceChange,
              removeFetchedLiabilityBalance, removeNewLiabilityBalanceRow,
              'Summa skulder och eget kapital', totalLiabilitiesEquity
            )}
            <button type="button" className="fyd-add-row" onClick={addNewLiabilityBalanceRow}>
              <Plus size={15} />
              Lägg till skuld/eget kapital
            </button>

            <div className={`fyd-banner ${isBalanced ? 'ok' : 'off'}`}>
              <span className="d-flex align-items-center gap-2">
                {isBalanced ? <Scale size={16} /> : <AlertCircle size={16} />}
                {isBalanced
                  ? 'Balansen stämmer — tillgångar är lika med skulder och eget kapital.'
                  : 'Balansen stämmer inte ännu.'}
              </span>
              {!isBalanced && (
                <span>Differens: {fmtSek(totalAssets - Math.abs(totalLiabilitiesEquity))}</span>
              )}
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="primary"
                onClick={saveOpeningBalances}
                disabled={!isBalanced}
              >
                <Save size={14} className="me-1" />
                Spara ingående balanser
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ReactTooltip place="right" effect="solid" id="redovisningsmetod" />
      <ReactTooltip place="right" effect="solid" id="momsperiod" />
    </>
  );
};

export default FiscalYearDetails;
